import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import "./GlobalModal.css";

export function GlobalModal({
  trigger,
  title,
  description,
  children,
  footer,
  size = "md",          // "sm" | "md" | "lg" | "xl"
  variant = "default",  // "default" | "primary" | "success" | "warning" | "danger" | "info"
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  initialOpen = false,
  open: controlledOpen, // Controlled state
  onOpen,
  onClose,
  classNames = {},
}) {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(Boolean(initialOpen));
  
  const open = isControlled ? controlledOpen : internalOpen;
  
  const titleId = useId();
  const backdropRef = useRef(null);
  const previousFocusRef = useRef(null);

  const handleOpen = useCallback(() => {
    previousFocusRef.current = document.activeElement;
    if (!isControlled) setInternalOpen(true);
    onOpen?.();
  }, [onOpen, isControlled]);

  const handleClose = useCallback(() => {
    if (!isControlled) setInternalOpen(false);
    onClose?.();
    previousFocusRef.current?.focus();
  }, [onClose, isControlled]);

  // Escape key
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeOnEscape, handleClose]);

  // Scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Focus trap
  useEffect(() => {
    if (!open) return;
    const el = backdropRef.current;
    if (!el) return;
    const focusable = el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();
    const trap = (e) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    el.addEventListener("keydown", trap);
    return () => el.removeEventListener("keydown", trap);
  }, [open]);

  const trigger_ = trigger ? React.cloneElement(trigger, { onClick: handleOpen }) : null;
  const sizeClass = size !== "md" ? ` gff-modal--${size}` : "";
  const variantClass = variant !== "default" ? ` gff-modal--${variant}` : "";

  return (
    <>
      {trigger_}
      {open && (
        <div
          ref={backdropRef}
          className={classNames.backdrop || "gff-modal-backdrop"}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          onClick={closeOnBackdrop ? (e) => { if (e.target === e.currentTarget) handleClose(); } : undefined}
        >
          <div className={`${classNames.modal || "gff-modal"}${sizeClass}${variantClass}`}>
            {(title || description) && (
              <div className={classNames.header || "gff-modal__header"}>
                <div>
                  {title && (
                    <p id={titleId} className={classNames.title || "gff-modal__title"}>
                      {title}
                    </p>
                  )}
                  {description && (
                    <p className={classNames.description || "gff-modal__description"}>
                      {description}
                    </p>
                  )}
                </div>
                {showCloseButton ? (
                  <button
                    type="button"
                    className={classNames.close || "gff-modal__close"}
                    onClick={handleClose}
                    aria-label="Close dialog"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                ) : null}
              </div>
            )}
            <div className={classNames.body || "gff-modal__body"}>{children}</div>
            {footer && (
              <div className={classNames.footer || "gff-modal__footer"}>{footer}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}